import { Box } from "@mui/material"
import { makeStyles } from "tss-react/mui-compat"
import { VideoPlatform } from "app-constants/constants"
import { createPath, ROUTE } from "app-constants/routing"
import SessionCard, { SessionCardAction, SessionCardStatus } from "components/sessions/SessionCard"
import { useConventionRegistration } from "contexts/ConventionRegistrationContext"
import useConventionSessionRealTimeCapacity from "hooks/sessions/useConventionSessionRealTimeCapacity"
import useRelativeTime from "hooks/useRelativeTime"
import useSearchParams from "hooks/useSearchParams"
import { api } from "lib/api"
import openWindowAsync from "lib/open-window-async"
import ConventionSession from "models/convention-api/v1/ConventionSession"
import { useRouter } from "next/router"
import pluralize from "pluralize"
import React, { useState } from "react"
import useAsyncEffect from "use-async-effect"

const useStyles = makeStyles()(
  (theme) => ({
    container: {
      width: "100%",
      [theme.breakpoints.up("sm")]: {
        width: "100%",
        marginLeft: theme.spacing(0.5),
        marginRight: theme.spacing(0.5),
      },
      [theme.breakpoints.up("md")]: {
        width: "100%",
        marginLeft: 0,
        marginRight: 0,
      },
    },
  })
)

interface SessionCardContainerProps {
  session: ConventionSession
  userSessionIds: Record<string, boolean>
  onSetReminder: (sessionId: string) => void
  onRemoveReminder: (sessionId: string) => void
}

export const SessionCardContainer = (props: SessionCardContainerProps) => {
  const { session, userSessionIds, onSetReminder, onRemoveReminder } = props

  const [isUpdatingReminderStatus, setIsUpdatingReminderStatus] = useState(false)
  const router = useRouter()
  const searchParams = useSearchParams()
  const sessionIdToRemind = searchParams.get("sessionIdToRemind")
  const sessionIdToRemoveReminder = searchParams.get("sessionIdToRemoveReminder")
  const sessionIdToJoin = searchParams.get("sessionIdToJoin")
  const { classes } = useStyles()
  const conventionSessionCapacity = useConventionSessionRealTimeCapacity(session)
  const conventionRegistration = useConventionRegistration()

  const isReminderSet = userSessionIds[session.id]

  const relativeTime = useRelativeTime({
    startAt: session.start_at,
    endAt: session.end_at,
  })

  const viewDetailsHref = createPath({
    path: ROUTE.CONVENTION_SESSION,
    params: {
      conventionId: router.query.convention_id as string,
      sessionId: session.id,
    },
  })

  const isLive = relativeTime.during

  useAsyncEffect(async () => {
    if (!sessionIdToJoin && !sessionIdToRemind && !sessionIdToRemoveReminder) return

    if (!conventionRegistration.isRegistered) {
      await conventionRegistration.openRegisterDialog()
      setIsUpdatingReminderStatus(false)
      return
    }

    if (sessionIdToRemind === session.id) {
      await onSetReminder(session.id)
      await searchParams.delete("sessionIdToRemind")
    }

    if (sessionIdToRemoveReminder === session.id) {
      await onRemoveReminder(session.id)
      await searchParams.delete("sessionIdToRemoveReminder")
    }

    if (sessionIdToJoin === session.id) {
      await searchParams.delete("sessionIdToRemoveReminder")
      if (session.video_platform === VideoPlatform.WHEREBY) {
        await router.push(
          createPath({
            path: ROUTE.CONVENTION_SESSION,
            params: {
              conventionId: router.query.convention_id as string,
              sessionId: session.id,
            },
            query: {
              sessionIdToJoin: session.id,
            },
          }),
        )
        return
      }

      await openWindowAsync(async () => {
        await api.joinSession({ req: undefined }, session.id)
        return session.integrated_meeting_url
      })
    }

    setIsUpdatingReminderStatus(false)
  }, [
    sessionIdToRemind,
    sessionIdToRemoveReminder,
    sessionIdToJoin,
    conventionRegistration.isRegistered,
    session.id,
    isUpdatingReminderStatus,
  ])

  const getCapacityStatusText = () => {
    if (!conventionSessionCapacity) return undefined
    const { zoomSeatsLeft, wherebyTotalInRoom } = conventionSessionCapacity

    if (session.video_platform === VideoPlatform.WHEREBY) {
      return `${wherebyTotalInRoom} ${pluralize("people", wherebyTotalInRoom)} in the room`
    }

    const MIN_VALUE_TO_SHOW_SEATS_STATUS = 20

    if (zoomSeatsLeft < MIN_VALUE_TO_SHOW_SEATS_STATUS) return undefined

    return `Only ${zoomSeatsLeft} ${pluralize("seat", zoomSeatsLeft)} left. `
  }

  const action = ((): SessionCardAction => {
    if (isUpdatingReminderStatus)
      return {
        status: SessionCardStatus.LOADING,
      }

    if (conventionSessionCapacity?.full)
      return {
        status: SessionCardStatus.AT_CAPACITY,
      }

    if (isLive)
      return {
        status: SessionCardStatus.LIVE,
        statusText: getCapacityStatusText(),
        onClick: () => {
          setIsUpdatingReminderStatus(true)
          searchParams.set("sessionIdToJoin", session.id)
        },
      }

    if (isReminderSet)
      return {
        status: SessionCardStatus.REGISTERED,
        onClick: () => {
          setIsUpdatingReminderStatus(true)
          searchParams.set("sessionIdToRemoveReminder", session.id)
        },
      }

    return {
      status: SessionCardStatus.REGISTRATION,
      onClick: () => {
        setIsUpdatingReminderStatus(true)
        searchParams.set("sessionIdToRemind", session.id)
      },
    }
  })()

  return (
    <Box className={classes.container}>
      <SessionCard session={session} href={viewDetailsHref} action={action} />
    </Box>
  )
}
